import React, { useEffect, useState } from 'react';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import Link from '@cloudscape-design/components/link';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import { apiJson } from '../utils/api';

interface DataSource {
  source_id: string;
  name: string;
  url: string;
  kind: string;
  match_make?: string;
  match_model?: string;
  match_category?: string;
  priority?: number;
  notes?: string;
  enabled?: boolean;
}

const KINDS = [
  { label: 'Type club', value: 'type-club' },
  { label: 'Association', value: 'association' },
  { label: 'Museum', value: 'museum' },
  { label: 'Manufacturer', value: 'manufacturer' },
  { label: 'General (always consulted)', value: 'general' },
];

interface SourceForm {
  name: string;
  url: string;
  kind: string;
  match_make: string;
  match_model: string;
  match_category: string;
  priority: string;
  notes: string;
}

const EMPTY: SourceForm = {
  name: '',
  url: '',
  kind: 'type-club',
  match_make: '',
  match_model: '',
  match_category: '',
  priority: '5',
  notes: '',
};

const DataSourcesPage: React.FC = () => {
  const [sources, setSources] = useState<DataSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<SourceForm>(EMPTY);

  const load = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiJson<{ items: DataSource[] }>('/admin/data-sources');
      setSources(data.items);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const seed = async (): Promise<void> => {
    setSeeding(true);
    setMessage(null);
    try {
      const data = await apiJson<{ message: string }>('/admin/data-sources/seed', {
        method: 'POST',
      });
      setMessage(data.message);
      await load();
    } catch (e) {
      setMessage(`Error: ${(e as Error).message}`);
    } finally {
      setSeeding(false);
    }
  };

  const create = async (): Promise<void> => {
    setCreating(true);
    setMessage(null);
    try {
      await apiJson('/admin/data-sources', {
        method: 'POST',
        body: JSON.stringify({
          name: form.name,
          url: form.url,
          kind: form.kind,
          match_make: form.match_make || undefined,
          match_model: form.match_model || undefined,
          match_category: form.match_category || undefined,
          priority: form.priority ? Number(form.priority) : undefined,
          notes: form.notes || undefined,
        }),
      });
      setMessage(`Added "${form.name}".`);
      setForm(EMPTY);
      await load();
    } catch (e) {
      setMessage(`Error: ${(e as Error).message}`);
    } finally {
      setCreating(false);
    }
  };

  const remove = async (s: DataSource): Promise<void> => {
    setMessage(null);
    try {
      await apiJson(`/admin/data-sources/${encodeURIComponent(s.source_id)}`, { method: 'DELETE' });
      setMessage(`Deleted "${s.name}".`);
      await load();
    } catch (e) {
      setMessage(`Error: ${(e as Error).message}`);
    }
  };

  const field = (key: keyof SourceForm) => (e: { detail: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.detail.value }));

  const canCreate = form.name && form.url && !creating;

  return (
    <ContentLayout header={<Header variant="h1">Story Data Sources</Header>}>
      <SpaceBetween size="l">
        {message && (
          <Alert type={message.startsWith('Error') ? 'error' : 'success'}>{message}</Alert>
        )}

        <Container
          header={
            <Header
              variant="h2"
              description="The curated sources the story agent consults when writing an aircraft's tour-guide story. A source applies when every set match field matches; leave them blank for an always-consulted general reference."
              actions={
                <Button onClick={() => void seed()} loading={seeding} iconName="add-plus">
                  Seed starter catalog
                </Button>
              }
            >
              Quick start
            </Header>
          }
        >
          <Box variant="p" color="text-body-secondary">
            Example: a type club like the International Cessna 170 Association (cessna170.org) matched
            to make “Cessna”, model “170”.
          </Box>
        </Container>

        <Container header={<Header variant="h2">Add a source</Header>}>
          <SpaceBetween size="m">
            <ColumnLayout columns={2}>
              <FormField label="Name">
                <Input
                  value={form.name}
                  onChange={field('name')}
                  placeholder="International Cessna 170 Association"
                />
              </FormField>
              <FormField label="URL">
                <Input value={form.url} onChange={field('url')} placeholder="https://cessna170.org/" />
              </FormField>
              <FormField label="Kind">
                <Select
                  selectedOption={KINDS.find((k) => k.value === form.kind) ?? KINDS[0]}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, kind: e.detail.selectedOption.value ?? 'type-club' }))
                  }
                  options={KINDS}
                />
              </FormField>
              <FormField label="Priority (higher = consulted first)">
                <Input value={form.priority} onChange={field('priority')} type="number" />
              </FormField>
              <FormField label="Match make">
                <Input value={form.match_make} onChange={field('match_make')} placeholder="Cessna" />
              </FormField>
              <FormField label="Match model (substring)">
                <Input value={form.match_model} onChange={field('match_model')} placeholder="170" />
              </FormField>
              <FormField label="Match category">
                <Input
                  value={form.match_category}
                  onChange={field('match_category')}
                  placeholder="warbird"
                />
              </FormField>
              <FormField label="Notes (guidance for the agent)">
                <Input
                  value={form.notes}
                  onChange={field('notes')}
                  placeholder="Heritage, member stories, fly-ins"
                />
              </FormField>
            </ColumnLayout>
            <Button
              variant="primary"
              onClick={() => void create()}
              loading={creating}
              disabled={!canCreate}
            >
              Add source
            </Button>
          </SpaceBetween>
        </Container>

        {error && <Alert type="error">{error}</Alert>}

        <Table
          loading={loading}
          loadingText="Loading data sources"
          items={sources}
          columnDefinitions={[
            { id: 'name', header: 'Name', cell: (s) => s.name },
            {
              id: 'url',
              header: 'URL',
              cell: (s) => (
                <Link href={s.url} external>
                  {s.url}
                </Link>
              ),
            },
            { id: 'kind', header: 'Kind', cell: (s) => s.kind },
            {
              id: 'match',
              header: 'Matches',
              cell: (s) =>
                [s.match_make, s.match_model, s.match_category].filter(Boolean).join(' · ') ||
                'all aircraft',
            },
            { id: 'priority', header: 'Priority', cell: (s) => s.priority ?? 0 },
            {
              id: 'actions',
              header: '',
              cell: (s) => (
                <Button variant="inline-link" iconName="remove" onClick={() => void remove(s)}>
                  Delete
                </Button>
              ),
            },
          ]}
          empty={<Box textAlign="center">No sources yet — seed the starter catalog or add one.</Box>}
          header={<Header counter={`(${sources.length})`}>Catalog</Header>}
          variant="full-page"
        />
      </SpaceBetween>
    </ContentLayout>
  );
};

export default DataSourcesPage;
